import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AlertTriangle, AlertCircle, Trash2, Loader2, CheckCircle2 } from 'lucide-react'

interface DeleteConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  title: string
  entityName: string
  entityType: string
  isIndependent: boolean
  blockingReason?: string
  dependencyDetails?: Array<{ label: string; count: number }>
}

export function DeleteConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  entityName,
  entityType,
  isIndependent,
  blockingReason,
  dependencyDetails = [],
}: DeleteConfirmDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    if (!isIndependent) return
    setIsDeleting(true)
    setError(null)
    try {
      await onConfirm()
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to delete item')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleClose = () => {
    if (isDeleting) return
    setError(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div
              className={`flex size-10 shrink-0 items-center justify-center rounded-full ${
                isIndependent ? 'bg-destructive/10 text-destructive' : 'bg-amber-500/10 text-amber-500'
              }`}
            >
              {isIndependent ? <Trash2 className="size-5" /> : <AlertTriangle className="size-5" />}
            </div>
            <div>
              <DialogTitle className="text-base font-bold">{title}</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Target {entityType}: <span className="font-semibold text-foreground">{entityName}</span>
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {!isIndependent ? (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3.5 space-y-2">
              <div className="flex items-center gap-2 text-xs font-bold text-amber-600 dark:text-amber-400">
                <AlertCircle className="size-4 shrink-0" />
                <span>Deletion Blocked (Has Dependent Records)</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {blockingReason ||
                  `This ${entityType.toLowerCase()} cannot be deleted because dependent child records exist. Only completely independent items can be deleted to protect catalog integrity.`}
              </p>

              {dependencyDetails.length > 0 && (
                <div className="pt-1 flex flex-wrap gap-2">
                  {dependencyDetails.map((dep, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded bg-amber-500/10 text-[11px] font-semibold text-amber-700 dark:text-amber-300 border border-amber-500/20"
                    >
                      {dep.count} {dep.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-accent/20 p-3.5 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="size-4 shrink-0" />
                <span>Item is Independent &amp; Safe to Delete</span>
              </div>
              <p className="text-xs text-muted-foreground">
                This {entityType.toLowerCase()} has zero dependent models, generations, variants, or listings. Are you
                sure you want to permanently remove it? This action cannot be undone.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/20 bg-destructive/10 p-2.5 text-xs text-destructive flex items-start gap-2">
              <AlertCircle className="size-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isDeleting}>
            {isIndependent ? 'Cancel' : 'Understood'}
          </Button>
          {isIndependent && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isDeleting}
              className="gap-2"
            >
              {isDeleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              {isDeleting ? 'Deleting...' : `Delete ${entityType}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
